import {SyncStatus} from '../offline/entity.js';
import {SMOKING_SESSION_ENTITY_TYPE} from './session-store.js';
import {LIVE_EVENT_ENTITY_TYPE} from '../live/live-event-store.js';
import {LIVE_STAGE_TRANSITION_ENTITY_TYPE} from '../live/stage-transition-store.js';

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function rejectedStageEvidence(row){
  if(row?.syncStatus!==SyncStatus.CONFLICT)return false;
  if(row?.conflict?.reason!=='parent_session_conflict')return false;
  if(row?.entityType===LIVE_STAGE_TRANSITION_ENTITY_TYPE)return true;
  return row?.entityType===LIVE_EVENT_ENTITY_TYPE&&row?.eventType==='stage_changed';
}

export function createLiveSessionConflictResolver({
  repository,
  sessions,
  remoteSession,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.put||!repository?.list||!repository?.remove){
    throw new TypeError('repository with put/list/remove is required');
  }
  if(!sessions?.get)throw new TypeError('session store is required');
  if(!remoteSession?.fetch)throw new TypeError('remote session fetch adapter is required');
  const owner=required(userId,'userId');

  async function acceptRemoteSession(id){
    const sessionId=required(id,'sessionId');
    const local=await sessions.get(sessionId);
    if(!local)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(local.userId!==owner)throw new Error('SMOKING_SESSION_OWNER_MISMATCH');
    if(local.syncStatus!==SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_NOT_IN_CONFLICT');

    const remote=await remoteSession.fetch(sessionId);
    if(!remote?.ok||!remote.entity)throw new Error('SMOKING_SESSION_REMOTE_MISSING');
    if(remote.entity.userId!==owner||remote.entity.id!==sessionId){
      throw new Error('SMOKING_SESSION_REMOTE_OWNER_MISMATCH');
    }

    const next={
      ...local,
      ...remote.entity,
      id:local.id,
      userId:owner,
      createdAt:local.createdAt,
      updatedAt:now(),
      syncStatus:SyncStatus.SYNCED,
      serverVersion:Number(remote.serverVersion)||1,
      syncAttempts:0,
      lastSyncAttemptAt:local.lastSyncAttemptAt||null,
      lastSyncFailureAt:null,
      lastSyncErrorCode:null,
      conflict:null
    };
    await repository.put(SMOKING_SESSION_ENTITY_TYPE,next);
    return Object.freeze(next);
  }

  async function listRejectedStageEvidence(sessionId){
    const id=required(sessionId,'sessionId');
    const [transitions,events]=await Promise.all([
      repository.list(LIVE_STAGE_TRANSITION_ENTITY_TYPE),
      repository.list(LIVE_EVENT_ENTITY_TYPE)
    ]);
    return Object.freeze([...transitions,...events]
      .filter(row=>row.userId===owner&&row.sessionId===id)
      .filter(rejectedStageEvidence)
      .sort((a,b)=>String(a.updatedAt||a.createdAt||'').localeCompare(String(b.updatedAt||b.createdAt||''))));
  }

  async function hasRejectedStageEvidence(sessionId){
    return (await listRejectedStageEvidence(sessionId)).length>0;
  }

  async function discardRejectedStageEvidence(sessionId){
    const rows=await listRejectedStageEvidence(sessionId);
    for(const row of rows){
      await repository.remove(row.entityType,row.id);
    }
    return Object.freeze({
      ok:true,
      status:'discarded',
      sessionId:required(sessionId,'sessionId'),
      discarded:Object.freeze(rows.map(row=>Object.freeze({entityType:row.entityType,id:row.id})))
    });
  }

  return Object.freeze({
    acceptRemoteSession,
    listRejectedStageEvidence,
    hasRejectedStageEvidence,
    discardRejectedStageEvidence
  });
}
